import { useEffect, useState } from 'react';
import {
  electronicDeliveryFileUrl,
  generateElectronicDelivery,
  getElectronicDelivery,
  type ElectronicDelivery,
  type ElectronicProfileType,
} from './api';

type Props = {
  companyId: string;
  csrfToken: string;
  onError: (error: unknown) => void;
  projectId: string;
  revisionId: string;
};

function profileLabel(value: ElectronicProfileType): string {
  if (value === 'HOKKAIDO_DEVELOPMENT_BUREAU') return '北海道開発局';
  if (value === 'HOKKAIDO_CONSTRUCTION_DEPARTMENT') return '北海道建設部';
  return '市町村';
}

export function ElectronicDeliveryPanel({
  companyId,
  csrfToken,
  onError,
  projectId,
  revisionId,
}: Props) {
  const [delivery, setDelivery] = useState<ElectronicDelivery | null>(null);
  const [busy, setBusy] = useState(false);
  const [key, setKey] = useState(() => crypto.randomUUID());
  const [notice, setNotice] = useState<string | null>(null);

  async function refresh(): Promise<void> {
    setDelivery(await getElectronicDelivery(companyId, projectId, revisionId));
  }

  useEffect(() => {
    void refresh().catch(onError);
  }, [companyId, projectId, revisionId]);

  async function generate(): Promise<void> {
    setBusy(true);
    setNotice('承認済みprofileをsnapshotし、PDF・manifest・検証レポートをZIPへ整理しています。');
    try {
      const next = await generateElectronicDelivery(
        companyId,
        projectId,
        revisionId,
        csrfToken,
        key,
      );
      setDelivery(next);
      setKey(crypto.randomUUID());
      setNotice('電子納品ソフトへ組み込みやすいZIPを検証し、R2へ非公開保存しました。');
    } catch (error) {
      onError(error);
      await refresh().catch(() => undefined);
      setNotice('生成に失敗しました。状態を確認して同じ工程から再実行できます。');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="electronic-delivery-panel stack">
      <div className="section-heading">
        <div>
          <p className="eyebrow">ELECTRONIC DELIVERY</p>
          <h4>電子納品組込用一式</h4>
        </div>
        <button className="secondary" disabled={busy} onClick={() => void refresh()} type="button">
          状態を更新
        </button>
      </div>
      <div className="notice warning">
        当該工事の事前協議および最新版電子納品要領を確認してください。本システムは電子成果品全体への適合を保証するものではありません。
      </div>
      {notice !== null && <div className="notice warning">{notice}</div>}
      {delivery === null ? (
        <div className="material-loading">電子納品profileを確認しています…</div>
      ) : (
        <>
          <dl className="detail-list compact-list">
            <div>
              <dt>発注者区分</dt>
              <dd>{profileLabel(delivery.requiredProfileType)}</dd>
            </div>
            <div>
              <dt>対象年月</dt>
              <dd>{delivery.targetMonth}</dd>
            </div>
            {delivery.requiredProfileType === 'MUNICIPALITY' && (
              <div>
                <dt>市町村名</dt>
                <dd>{delivery.municipalityName ?? '未設定'}</dd>
              </div>
            )}
          </dl>
          {delivery.effectiveProfile === null ? (
            <div className="notice warning">
              対象年月に有効な承認済みprofileがありません。「電子納品profile」タブで公式情報を確認し、該当版を承認してください。
            </div>
          ) : (
            <>
              <p>
                使用profile: <strong>{delivery.effectiveProfile.version}</strong>（確認日{' '}
                {delivery.effectiveProfile.verifiedAt?.slice(0, 10) ?? '未確認'}）
              </p>
              <a href={delivery.effectiveProfile.sourceUrl} rel="noreferrer" target="_blank">
                profileの公式情報入口を確認
              </a>
              <button disabled={busy} onClick={() => void generate()} type="button">
                {delivery.job?.status === 'FAILED'
                  ? '途中工程から再実行'
                  : '電子納品組込用ZIPを生成'}
              </button>
            </>
          )}
          {delivery.job !== null && (
            <div className={`slides-export-status ${delivery.job.status.toLowerCase()}`}>
              <strong>生成状態: {delivery.job.status}</strong>
              <span>
                {profileLabel(delivery.job.profileType)} / {delivery.job.profileVersion}
              </span>
              {delivery.job.errorDetail !== null && <p>{delivery.job.errorDetail}</p>}
              {delivery.job.status === 'VERIFIED' && delivery.job.fileId !== null && (
                <>
                  <p>
                    {delivery.job.displayName}（
                    {delivery.job.sizeBytes === null
                      ? 'size確認中'
                      : `${Math.ceil(delivery.job.sizeBytes / 1024)} KiB`}
                    ）
                  </p>
                  <a
                    href={electronicDeliveryFileUrl(
                      companyId,
                      projectId,
                      revisionId,
                      delivery.job.fileId,
                    )}
                  >
                    電子納品組込用ZIPを確認
                  </a>
                </>
              )}
            </div>
          )}
        </>
      )}
    </section>
  );
}
